import React, { useCallback, useEffect, useState } from "react";

import AuthService from "../services/auth.service";
import UserService from "../services/user.service";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Spinner from "./Spinner";

const ALL_ROLES = ["user", "moderator", "admin"];
const PAGE_SIZE = 10;

const UserManagement = () => {
  const { t } = useTranslation();
  const currentUser = AuthService.getCurrentUser();

  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Per-row edit state keyed by user id.
  const [editing, setEditing] = useState(null); // { id, email, roles[], password }

  const load = useCallback(
    (opts = {}) => {
      const p = opts.page || page;
      const query = opts.q !== undefined ? opts.q : q;
      setLoading(true);
      UserService.getAll({ q: query, page: p, size: PAGE_SIZE })
        .then((res) => {
          setUsers(res.data.users || []);
          setPages(res.data.pages || 1);
          setTotal(res.data.total || 0);
          setPage(res.data.page || 1);
        })
        .catch((err) =>
          toast.error(
            (err.response && err.response.data && err.response.data.message) ||
              err.message
          )
        )
        .finally(() => setLoading(false));
    },
    [page, q]
  );

  useEffect(() => {
    load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = (e) => {
    e.preventDefault();
    load({ page: 1, q });
  };

  const beginEdit = (u) => {
    setEditing({ id: u.id, email: u.email, roles: [...u.roles], password: "" });
  };
  const cancelEdit = () => setEditing(null);

  const toggleRole = (role) => {
    setEditing((e) => {
      const has = e.roles.includes(role);
      return {
        ...e,
        roles: has ? e.roles.filter((r) => r !== role) : [...e.roles, role],
      };
    });
  };

  const saveEdit = () => {
    const payload = { email: editing.email, roles: editing.roles };
    if (editing.password) payload.password = editing.password;
    UserService.update(editing.id, payload)
      .then(() => {
        toast.success(t("users.saved"));
        setEditing(null);
        load();
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      );
  };

  const removeUser = (u) => {
    if (!window.confirm(t("users.confirmDelete", { name: u.username }))) return;
    UserService.remove(u.id)
      .then(() => {
        toast.success(t("users.deleted"));
        load();
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      );
  };

  const roleBadge = (r) => {
    const cls =
      r === "admin" ? "bg-danger" : r === "moderator" ? "bg-warning text-dark" : "bg-secondary";
    return (
      <span key={r} className={`badge ${cls} me-1`}>
        {r}
      </span>
    );
  };

  return (
    <div className="container my-4">
      <h3 className="mb-3">{t("users.title")}</h3>

      <form className="row g-2 mb-3" onSubmit={search}>
        <div className="col-sm-8 col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder={t("users.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-outline-primary px-5" type="submit">
            {t("users.search")}
          </button>
        </div>
        {q && (
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setQ("");
                load({ page: 1, q: "" });
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </form>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <Spinner small />
          ) : users.length === 0 ? (
            <p className="text-muted mb-0">{t("users.empty")}</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>{t("users.username")}</th>
                    <th>{t("users.email")}</th>
                    <th style={{ width: 220 }}>{t("users.roles")}</th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) =>
                    editing && editing.id === u.id ? (
                      <tr key={u.id} className="table-active">
                        <td>{u.id}</td>
                        <td>{u.username}</td>
                        <td>
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            value={editing.email}
                            onChange={(e) =>
                              setEditing({ ...editing, email: e.target.value })
                            }
                          />
                          <input
                            type="password"
                            className="form-control form-control-sm mt-1"
                            placeholder={t("users.newPassword")}
                            value={editing.password}
                            onChange={(e) =>
                              setEditing({ ...editing, password: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          {ALL_ROLES.map((r) => (
                            <div className="form-check form-check-inline" key={r}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`role-${u.id}-${r}`}
                                checked={editing.roles.includes(r)}
                                onChange={() => toggleRole(r)}
                              />
                              <label
                                className="form-check-label small"
                                htmlFor={`role-${u.id}-${r}`}
                              >
                                {r}
                              </label>
                            </div>
                          ))}
                        </td>
                        <td className="text-nowrap">
                          <button
                            className="btn btn-sm btn-success me-1"
                            onClick={saveEdit}
                          >
                            {t("common.update")}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={cancelEdit}
                          >
                            {t("common.cancel")}
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>
                          {u.username}
                          {currentUser && currentUser.id === u.id && (
                            <span className="badge bg-info text-dark ms-1">
                              {t("users.you")}
                            </span>
                          )}
                        </td>
                        <td>{u.email}</td>
                        <td>{u.roles.map(roleBadge)}</td>
                        <td className="text-nowrap">
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => beginEdit(u)}
                          >
                            {t("common.edit")}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={currentUser && currentUser.id === u.id}
                            onClick={() => removeUser(u)}
                          >
                            {t("common.delete")}
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <nav className="mt-3">
          <ul className="pagination justify-content-center mb-0">
            <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => load({ page: page - 1 })}
              >
                ‹
              </button>
            </li>
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
                <button className="page-link" onClick={() => load({ page: p })}>
                  {p}
                </button>
              </li>
            ))}
            <li className={`page-item ${page >= pages ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => load({ page: page + 1 })}
              >
                ›
              </button>
            </li>
          </ul>
        </nav>
      )}

      <p className="text-muted small mt-2 text-center">
        {t("users.totalCount", { count: total })}
      </p>
    </div>
  );
};

export default UserManagement;
